import { MarkerF, OverlayView, OverlayViewF } from "@react-google-maps/api";
import { memo, useCallback, useState } from "react";
import { ActiveRequest } from "@ses-mams/api-contract";
import eventPinImage from "~/assets/pins/event.png";
import { tsr } from "~/utils/client";
import { Text } from "~/components/ui/text";
import { Stack } from "~/components/ui/stack";
import { IconButton } from "~/components/ui/button";
import { XOutlineIcon } from "~/components/ui/icon";

export const ActiveRequestMarkers = memo(() => {
  const [selected, setSelected] = useState<ActiveRequest | null>(null);

  const { data } = tsr.reporting.listActiveRequests.useQuery({
    queryKey: ["reporting", "active-requests"],
    refetchInterval: 60000, // 1 minute
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const activeRequests = data?.body;

  const handleClose = useCallback(() => {
    setSelected(null);
  }, []);

  if (!activeRequests) {
    return null;
  }

  return activeRequests.map(request => (
    <ActiveRequestMarker
      key={request.id}
      request={request}
      selected={selected?.id === request.id}
      onClick={() => setSelected(request)}
      onClose={handleClose}
    />
  ));
});

type ActiveRequestMarkerProps = {
  request: ActiveRequest;
  selected: boolean;
  onClick: () => void;
  onClose: () => void;
};

const ActiveRequestMarker = ({
  selected,
  request: { type, title, location, latitude, longitude },
  onClick,
  onClose,
}: ActiveRequestMarkerProps) => {
  if (typeof latitude === "undefined" || typeof longitude === "undefined") {
    return null;
  }

  return (
    <>
      <MarkerF
        position={{ lat: latitude, lng: longitude }}
        icon={{ url: eventPinImage }}
        zIndex={10}
        onClick={onClick}
      />
      {selected ? (
        <OverlayViewF
          mapPaneName={OverlayView.FLOAT_PANE}
          position={{ lat: latitude, lng: longitude }}
          getPixelPositionOffset={(width, height) => ({
            x: -(width / 2),
            y: -1 * height - 40,
          })}
        >
          <Stack
            position="relative"
            background="surface"
            borderRadius="large"
            padding="medium"
            sx={{ boxShadow: "0px 2px 8px -3px #00000033;" }}
          >
            <IconButton
              size="small"
              aria-label="Close"
              onClick={onClose}
              sx={{ position: "absolute", top: 0, right: 0 }}
            >
              <XOutlineIcon />
            </IconButton>
            <Text size="small" tone="muted">
              {mapTypeToLabel[type]}
            </Text>
            <Text size="large" weight="semi">
              {title}
            </Text>
            {location && <Text size="small">{location}</Text>}
          </Stack>
        </OverlayViewF>
      ) : null}
    </>
  );
};

const mapTypeToLabel = {
  activation: "Activation",
  activity: "Activity",
  "out-of-area-activation": "OOAA",
} as const;
